import React, { useMemo, useState } from 'react';
import { Box, Typography, TextField, FormControlLabel, Checkbox, Chip, FormGroup } from '@mui/material';
import TranscriptTurn from './TranscriptTurn';

/**
 * Read-only structured transcript viewer with speaker filter, review-only
 * filtering, text search, and timestamp navigation callbacks.
 *
 * Turns stay read-only until stable turn IDs, revision control, and a
 * correction endpoint exist.
 */
const TranscriptViewer = ({ turns, onSeek }) => {
  const [search, setSearch] = useState('');
  const [reviewOnly, setReviewOnly] = useState(false);
  const [activeSpeakers, setActiveSpeakers] = useState([]);

  const speakers = useMemo(() => {
    const seen = new Map();
    turns.forEach((turn) => {
      const name = turn.speakerName || 'Unknown speaker';
      if (!seen.has(name)) {
        seen.set(name, { name, count: 0 });
      }
      seen.get(name).count += 1;
    });
    return [...seen.values()];
  }, [turns]);

  const toggleSpeaker = (name) => {
    setActiveSpeakers((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  };

  const filteredTurns = useMemo(() => {
    const query = search.trim().toLowerCase();
    return turns.filter((turn) => {
      if (reviewOnly && !turn.review?.flagged) return false;
      if (activeSpeakers.length > 0) {
        const name = turn.speakerName || 'Unknown speaker';
        if (!activeSpeakers.includes(name)) return false;
      }
      if (query && !turn.text.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [turns, search, reviewOnly, activeSpeakers]);

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} mb={2}>
        <TextField
          size="small"
          fullWidth
          label="Search transcript"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
        />
        <FormGroup row sx={{ alignItems: 'center', flexShrink: 0 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={reviewOnly}
                onChange={(e) => setReviewOnly(e.target.checked)}
                size="small"
              />
            }
            label="Flagged only"
          />
        </FormGroup>
      </Box>

      {speakers.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          {speakers.map((speaker) => {
            const active = activeSpeakers.includes(speaker.name);
            return (
              <Chip
                key={speaker.name}
                label={`${speaker.name} (${speaker.count})`}
                size="small"
                color={active ? 'primary' : 'default'}
                variant={active ? 'filled' : 'outlined'}
                onClick={() => toggleSpeaker(speaker.name)}
              />
            );
          })}
        </Box>
      )}

      {filteredTurns.length === 0 ? (
        <Typography color="textSecondary" sx={{ py: 3 }} align="center">
          No transcript turns match your filters.
        </Typography>
      ) : (
        <Box>
          {filteredTurns.map((turn) => (
            <TranscriptTurn key={turn.id || `${turn.startSeconds}-${turn.text.slice(0, 20)}`} turn={turn} onTimestampClick={onSeek} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TranscriptViewer;